import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TIMEZONES = ["Europe/Paris", "Europe/Brussels", "Europe/Geneva", "Europe/Luxembourg", "America/Montreal", "Africa/Casablanca", "Africa/Tunis", "Africa/Abidjan"];
const COUNTRIES = [
  { code: "FR", label: "France" },
  { code: "BE", label: "Belgique" },
  { code: "CH", label: "Suisse" },
  { code: "LU", label: "Luxembourg" },
  { code: "CA", label: "Canada" },
  { code: "MA", label: "Maroc" },
  { code: "TN", label: "Tunisie" },
  { code: "CI", label: "Côte d'Ivoire" },
];

export default function Onboarding() {
  const { reloadTenant } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", slug: "", timezone: "Europe/Paris", country_code: "FR" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await API.post("/tenants", form);
      await reloadTenant();
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-xl" data-testid="onboarding-card">
        <div className="mb-6 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600">
            <CalendarClock className="h-5 w-5 text-white" />
          </div>
          <span className="font-display text-xl font-bold text-slate-900">RendezVous</span>
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Créez votre entreprise</h1>
        <p className="mt-1 mb-6 text-sm text-slate-500">Votre espace de réservation sera prêt en quelques secondes.</p>
        <form onSubmit={submit} className="space-y-4">
          {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="onboarding-error">{error}</p>}
          <div>
            <Label htmlFor="name">Nom de l'entreprise</Label>
            <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Garage Dupont" data-testid="onboarding-name" className="mt-1" />
          </div>
          <div>
            <Label htmlFor="slug">Identifiant public (facultatif)</Label>
            <Input id="slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="garage-dupont" data-testid="onboarding-slug" className="mt-1" />
            <p className="mt-1 text-xs text-slate-400">Votre lien : /book/votre-identifiant</p>
          </div>
          <div>
            <Label>Fuseau horaire</Label>
            <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
              <SelectTrigger data-testid="onboarding-timezone" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Pays</Label>
            <Select value={form.country_code} onValueChange={(v) => setForm({ ...form, country_code: v })}>
              <SelectTrigger data-testid="onboarding-country" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="onboarding-submit">
            {loading ? "Création..." : "Créer mon entreprise"}
          </Button>
        </form>
      </div>
    </div>
  );
}
